import { useState } from "react";

import { useHistory } from "react-router-dom";
import { Box } from "@mui/material";

import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { useErrorSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { Stepper } from "../../../../Components/Stepper";
import { type Step } from "../../../../Components/Stepper/Types";
import { useAuthContext } from "../../../../Context/AuthContext";
import { useUserContext } from "../../../../Context/UserContext";
import { customersRoute } from "../../../../Navigation/core/utils";
import { useOnboardCloudZero } from "./hooks";
import ApiKeyStep from "./Steps/ApiKeyStep";
import OrganizationDetailsStep from "./Steps/OrganizationDetailsStep";
import { type FormState } from "./types";

export default function CloudZeroOnboarding() {
  const history = useHistory();
  const customerId = useCustomerId();
  const { userRoles } = useUserContext();
  const { isDoitEmployee } = useAuthContext();
  const hasPermissionToOnboard = userRoles?.billingProfilesAdmin || isDoitEmployee;
  const showErrorSnackbar = useErrorSnackbar();
  const { onboard, onboarding } = useOnboardCloudZero();

  const [formState, setFormState] = useState<FormState>({
    orgName: "",
    apiKey: "",
    fetchCosts: true,
    fetchInsights: true,
    connectionSuccess: false,
    connectionMessage: "",
    loading: false,
  });

  const returnToHomePage = () => {
    history.push(`${customersRoute(customerId)}/integrate/assets/cloudzero`);
  };

  const handleOnboard = async () => {
    const onboardingResult = await onboard({
      apiKey: formState.apiKey,
      orgName: formState.orgName,
      fetchCosts: formState.fetchCosts,
      fetchInsights: formState.fetchInsights,
    });

    if (onboardingResult.success) {
      returnToHomePage();
    } else {
      setFormState({
        ...formState,
        connectionSuccess: onboardingResult.success,
        connectionMessage: onboardingResult.message,
      });
    }
  };

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    setFormState({
      ...formState,
      [field]: value,
      connectionSuccess: false,
      connectionMessage: "",
    });
  };

  const steps: Step[] = [
    {
      children: <OrganizationDetailsStep formState={formState} onChange={handleChange} />,
      label: "Organization Details",
      order: 0,
      required: true,
      state: formState.orgName !== "" && (formState.fetchCosts || formState.fetchInsights) ? "complete" : "incomplete",
    },
    {
      children: <ApiKeyStep formState={formState} onChange={setFormState} />,
      label: "API Key",
      order: 1,
      required: true,
      state: formState.apiKey !== "" && formState.connectionSuccess ? "complete" : "incomplete",
    },
  ];

  return (
    <Box mt={6} mb={9}>
      <Stepper
        loading={formState.loading || onboarding}
        onCancel={returnToHomePage}
        onSubmit={handleOnboard}
        nextButtonClickHandler={async () => {
          if (!hasPermissionToOnboard) {
            showErrorSnackbar("Permission Denied: Billing Profile Admin role required");
            return false;
          }
          return true;
        }}
        steps={steps}
        submitButtonLabel={formState.connectionSuccess ? "Finish" : undefined}
        contentSx={{
          mt: 4,
          mb: 3,
        }}
        footerMaxWidth={860}
        maxWidth={1600}
      />
    </Box>
  );
}
